import { useCallback, useEffect, useState } from "react";
import {
  dateInputToSinceIso,
  dateInputToUntilIso,
} from "@/lib/activityTimeline/forensicsDateRange";
import {
  getInboundReceipt,
  listInboundReceipts,
  type InboundReceiptDetail,
  type InboundReceiptRow,
  type ReceiptSourceOption,
} from "@/lib/inboundReceiptsClient";

export type InboundReceiptsFilterState = {
  source: string;
  processingStatus: string;
  unlinkedOnly: boolean;
  sinceDate: string;
  untilDate: string;
};

export function initialInboundReceiptsFilters(): InboundReceiptsFilterState {
  return {
    source: "",
    processingStatus: "",
    unlinkedOnly: false,
    sinceDate: "",
    untilDate: "",
  };
}

export function useInboundReceipts(opts: { enabled: boolean; organizationId: string }) {
  const { enabled, organizationId } = opts;
  const [filters, setFilters] = useState<InboundReceiptsFilterState>(initialInboundReceiptsFilters);
  const [events, setEvents] = useState<InboundReceiptRow[]>([]);
  const [sources, setSources] = useState<ReceiptSourceOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [payloadById, setPayloadById] = useState<Record<string, InboundReceiptDetail>>({});
  const [payloadBusyId, setPayloadBusyId] = useState<string | null>(null);

  const patchFilters = useCallback((patch: Partial<InboundReceiptsFilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialInboundReceiptsFilters());
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled || !organizationId) {
      setEvents([]);
      setSources([]);
      return;
    }
    setLoading(true);
    setError(null);
    const r = await listInboundReceipts({
      organization_id: organizationId,
      source: filters.source || undefined,
      processing_status: filters.processingStatus || undefined,
      unlinked_only: filters.unlinkedOnly || undefined,
      since: dateInputToSinceIso(filters.sinceDate),
      until: dateInputToUntilIso(filters.untilDate),
    });
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      setEvents([]);
      return;
    }
    setEvents(r.data.events ?? []);
    setSources(r.data.sources ?? []);
  }, [enabled, organizationId, filters]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    setExpandedId(null);
    setPayloadById({});
  }, [organizationId]);

  const expandRow = useCallback(
    async (id: string) => {
      if (expandedId === id) {
        setExpandedId(null);
        return;
      }
      setExpandedId(id);
      if (payloadById[id] || !organizationId) return;
      setPayloadBusyId(id);
      const r = await getInboundReceipt(organizationId, id);
      setPayloadBusyId(null);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setPayloadById((prev) => ({ ...prev, [id]: r.data.event }));
    },
    [expandedId, organizationId, payloadById]
  );

  return {
    filters,
    patchFilters,
    resetFilters,
    events,
    sources,
    loading,
    error,
    refresh,
    expandedId,
    expandRow,
    payloadById,
    payloadBusyId,
  };
}
